import { Component, OnDestroy, OnInit } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@app/shared/models/page-event';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { HelperService } from './services/helper.service';
import { GemisTenant, NotificationStatus, PrivilegeAccess, Roles, Tenant, UserStatus } from '@app/shared/enums';
import { FormControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { trim, map as lodashMap, values, keys, startsWith } from 'lodash';
import { InviteUsersDialogComponent } from './dialog/invite-users-dialog/invite-users-dialog.component';
import { of, Subscription } from 'rxjs';
import { ExcelService } from '@app/shared/services/api/excel.service';
import moment, { MomentInput } from 'moment';
import { saveFile } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { ExtendedUserData, ExtendedUsersResponse } from '@app/shared/models/user';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { GERMAN_DATE_FORMAT } from '@app/shared/constants';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
  statusFilters = [UserStatus.active, UserStatus.activationPending, UserStatus.any];
  selectedStatus = UserStatus.any;
  searchControl = new FormControl('');
  emptyUser = false;
  users: ExtendedUserData[] = [];
  loading = false;
  count = 1;
  pageNumber = 1;
  pageSize = 5;
  orderBy = 'email asc';
  searchSubscription: Subscription = new Subscription();

  constructor(
    public helperService: HelperService,
    public userService: UserService,
    public matDialog: MatDialog,
    public overlayLoader: OverlayLoaderService,
    public authService: AuthService,
    private excelService: ExcelService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.searchSubscription = this.searchControl.valueChanges
      .pipe(
        startWith(''),
        map((value: string | null) => value || ''),
        distinctUntilChanged(),
        debounceTime(500),
        filter((a: string) => trim(a).length <= 0)
      )
      .subscribe(() => {
        this.overlayLoader.isLoading = false;
        this.resetQueryParameters();
        this.getUsers();
      });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  public statusFilterChange(value: UserStatus) {
    this.resetQueryParameters();
    this.selectedStatus = value;
    this.getUsers();
  }

  public onSearchClick() {
    this.resetQueryParameters();
    this.getUsers();
  }

  public onPageChange(pageEvent: PageEvent) {
    this.pageNumber = pageEvent.index as number;
    this.getUsers();
  }

  public onEnterKeyDown() {
    this.resetQueryParameters();
    this.getUsers();
  }

  public onSortChange(sort: Sort) {
    this.orderBy = `${sort.active} ${sort.direction}`;
    this.getUsers();
  }

  public resetQueryParameters() {
    this.count = 1;
    this.pageNumber = 1;
  }

  getUsers() {
    this.emptyUser = false;
    this.loading = true;
    this.users = [];
    this.userService
      .getUsers(this.getQueryFilters)
      .pipe(take(1))
      .subscribe({
        next: (u) => {
          this.count = u.maxRowCount;
          this.users = u.data;
          this.emptyUser = this.users.length === 0 ? !this.emptyUser : this.emptyUser;
          this.loading = false;
        },
        error: (err) => {
          if (err.status === 404) {
            this.count = 0;
          }
          this.loading = false;
        },
      });
  }

  private get getQueryFilters() {
    let q = '';

    if (this.selectedStatus === UserStatus.any) {
      q += `?$filter=status in ('${UserStatus.active}','${UserStatus.activationPending}') `;
    } else {
      q += `?$filter=status eq '${this.selectedStatus}' `;
    }

    const searchText = encodeURIComponent(trim(this.searchControl.value as string));
    if (searchText.length > 0) {
      q += `and (`;
      q += `contains(email, '${searchText}') `;
      q += `or contains(role/Name, '${searchText}') `;
      q += `or contains(AssignedMunicipality/Name, '${searchText}') `;
      q += `or (UserRegions/any(a:contains(a/Region/name, '${searchText}')))`;
      q += `) `;
    }

    q += `and role/code in ('${Roles.technicalDsoExpert}', '${Roles.technicalDsoEmployee}') eq false`;

    q += '&$expand=DelegateUser,';
    q += 'UserRegions($select=Region;$expand=Region($select=name)),';
    q += 'Role($select=Name,Code,RoleLevel,ManagedRoles),';
    q += 'AssignedMunicipality($select=Name,municipalityKey),';
    q += 'CustomerSystemIdentities($select=systemName,systemUserId)';

    q += `&$skip=${(this.pageNumber - 1) * this.pageSize}`;
    q += `&$top=${this.pageSize}`;
    q += `&$orderby=${this.orderBy}`;
    q += '&$count=true';
    return q;
  }

  public trackByFn(_: number, item: UserStatus) {
    return item;
  }

  public openInviteModal() {
    this.matDialog
      .open(InviteUsersDialogComponent, {
        panelClass: ['-md'],
        data: {
          inviteUserDialogTitle: this.translate.instant('module.modals.inviteWorker.inviteUsersHeaderText'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'reload') {
          this.resetQueryParameters();
          this.getUsers();
        }
      });
  }

  public downloadUserList() {
    this.overlayLoader.isLoading = true;
    let url = '';

    url += `?$filter=status in ('${UserStatus.active}','${UserStatus.activationPending}') and role/code ne '${Roles.technicalDsoExpert}' and role/code ne '${Roles.technicalDsoEmployee}'`;

    url += '&$select=Firstname,Lastname,Email,IsPriviledgedAccess,Status,InvitationDate,RegistrationDate,LastLoginDate,AllowNewsletter,';
    url += 'Gender,Title,IDPName,MalFunctionNotificationStatus,AllowToReceiveWorkOrderEmails,AllowToReceiveNewDocumentsEmails,';
    url += 'JobFunctionInfo,Department,Comment';

    url += '&$expand=jobType($select=jobTitle),';
    url += 'DelegateUser($select=Firstname,Lastname,EMail),';
    url +=
      'UserRegions($select=Region;$expand=Region($select=name;$expand=RegionMunicipalities($expand=Municipality($select=okoisPreferred);$select=municipality))),';
    url += 'Role($select=Name,Code,RoleLevel,ManagedRoles),';
    url += 'AssignedMunicipality($select=Name,municipalityKey,okoisPreferred),';
    url += 'municipalities($select=okoisPreferred),';
    url += `customerSystemIdentities($filter=systemName eq 'GEMIS';$select=systemUserId)`;

    url += '&$orderby=email asc';
    url += '&$count=true';

    this.userService
      .getUsers(url)
      .pipe(
        take(1),
        map((a: ExtendedUsersResponse) => this.formatData(a)),
        switchMap((a) => this.generateExcel(a)),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe({
        next: (xlsx) => {
          if (xlsx !== null) {
            const blob = new Blob([xlsx], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const roleLabel = this.authService.currentUser.roleName;
            const fileName = `userliste_${roleLabel}_${moment().format('DD-MM-YYYY')}.xlsx`;
            saveFile(blob, fileName);
          }
          this.overlayLoader.isLoading = false;
        },
        error: () => (this.overlayLoader.isLoading = false),
      });
  }

  private formatData(u: ExtendedUsersResponse) {
    const formatDateFunc = (date: MomentInput): string | null => {
      return date ? moment(date).format(GERMAN_DATE_FORMAT.display.dateInput) : null;
    };

    const formatActiveStatus = (isTrue: boolean | null): NotificationStatus => {
      return isTrue ? NotificationStatus.ACTIVE : NotificationStatus.INACTIVE;
    };

    const formatOptionalText = (text: string | null | undefined): string | null => {
      return text?.trim() ? text : null;
    };

    const isMeterEntryOkoEnabled = environment.features.meterEntryOko;
    const isBagTenant = environment.tenant?.trim().toLowerCase() === Tenant.BAYERNWERK;
    const isGemisTenant = (Object.values(GemisTenant) as string[]).includes(environment.tenant?.trim().toLowerCase());

    return u.data.map((a: ExtendedUserData) => {
      const hasAllMunicipalities =
        a.rolesCode === Roles.systemAdministrator ||
        a.rolesCode === Roles.callCenterAgent ||
        a.rolesCode === Roles.meterPointExpert ||
        (startsWith(a.rolesCode, 'PO') && a.rolesCode === Roles.poHolding);

      const gemisId = a.customerSystemIdentities?.[0]?.systemUserId ?? null;
      const sbPwaStatus = a.malFunctionNotificationStatus && a.malFunctionNotificationStatus !== '0' ? NotificationStatus.ACTIVE : NotificationStatus.INACTIVE;
      const okoStatus = this.getOkoStatus(a);

      const userExportData: { [key: string]: string | null } = {
        ['Anrede']: a.gender,
        ['Titel']: a.title,
        ['Vorname']: a.firstname,
        ['Nachname']: a.lastname,
        ['EMail']: a.email,
        ['Rolle']: a.rolesName,
        ['KID']: a.idpname,
        ...(isGemisTenant && { ['GemIS ID']: gemisId }),
        ['Privilegierter Zugriff']: a.isPriviledgedAccess ? PrivilegeAccess.YES : PrivilegeAccess.NO,
        ['Region']: hasAllMunicipalities || a.rolesCode === Roles.municipalityWorker ? null : trim(a.regions).split(',').join('\r\n'),
        ['AGS']: hasAllMunicipalities || a.rolesCode !== Roles.municipalityWorker ? null : a.municipalityKey,
        ['Kommune']: hasAllMunicipalities ? 'Alle Gemeinden' : a.rolesCode === Roles.municipalityWorker ? a.municipalityName : null,
        ['Status']: this.helperService.getStatusEquivalent(a.status),
        ['Eingeladen am']: formatDateFunc(a.invitationDate),
        ['Registriert am']: formatDateFunc(a.registrationDate),
        ['Letzter Login']: formatDateFunc(a.lastLoginDate),
        ['Newsletter']: formatActiveStatus(a.allowNewsletter),
        ...(isBagTenant && { ['SB PWA']: sbPwaStatus }),
        ...(isBagTenant && { ['SB E-Mail Schaden erl.']: formatActiveStatus(a.allowToReceiveWorkOrderEmails) }),
        ['MyDocs E-Mail']: formatActiveStatus(a.allowToReceiveNewDocumentsEmails),
        ...(isMeterEntryOkoEnabled && { ['Zählerstand E-Mail']: okoStatus }),
        ['Funktion']: formatOptionalText(a.jobType?.jobTitle),
        ['Funktionszusatz']: formatOptionalText(a.jobFunctionInfo),
        ['Abteilung']: formatOptionalText(a.department),
        ['Kommentar']: formatOptionalText(a.comment),
      };

      return userExportData;
    });
  }

  private getOkoStatus(user: ExtendedUserData): NotificationStatus {
    if (user.assignedMunicipality) {
      return user.assignedMunicipality.okoisPreferred ? NotificationStatus.ACTIVE : NotificationStatus.INACTIVE;
    }

    if (user.municipalities && user.municipalities.length > 0) {
      const allAreActive = user.municipalities.every((m) => m.okoisPreferred === true);
      if (allAreActive) return NotificationStatus.ACTIVE;

      const allAreInactive = user.municipalities.every((m) => m.okoisPreferred === false);
      if (allAreInactive) return NotificationStatus.INACTIVE;

      return NotificationStatus.NOT_SPECIFIED;
    }

    if (user.userRegions && user.userRegions.length > 0) {
      const allAreActive = user.userRegions.every((ur) => ur.region?.regionMunicipalities?.every((rm) => rm.municipality?.okoisPreferred === true));
      if (allAreActive) return NotificationStatus.ACTIVE;

      const allAreInactive = user.userRegions.every((ur) => ur.region?.regionMunicipalities?.every((rm) => rm.municipality?.okoisPreferred === false));
      if (allAreInactive) return NotificationStatus.INACTIVE;

      return NotificationStatus.NOT_SPECIFIED;
    }

    return NotificationStatus.NOT_SPECIFIED;
  }

  generateExcel(data: any) {
    if (data.length <= 0) {
      return of(null);
    }

    return this.excelService.generateExcel({
      fileName: `EnergiePortal_Benutzerliste_${moment().format('DD-MM-YYYY')}_Admin.xlsx`,
      sheetName: `_Benutzerliste_${moment().format('DD-MM-YYYY')}`,
      headers: keys(data[0]),
      data: lodashMap(data, values),
      protectedColumns: [],
      templateCode: '',
      headerBgColor: cssVariables['--ep-primary-color'],
      secretPhrase: '',
    });
  }
}
